import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const TABS = [
  { type: 'course_type', label: '車種 / 課程類別', placeholder: '例：棍波-私家車' },
  { type: 'student_category', label: '學員類型 / 專長', placeholder: '例：初考' },
  { type: 'source', label: '學員來源', placeholder: '例：Facebook' },
  { type: 'exam_venue', label: '考試場地', placeholder: '例：永孝街' },
];

const AdminCategories = () => {
  const [activeType, setActiveType] = useState('course_type');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formName, setFormName] = useState('');

  useEffect(() => {
    fetchCategories(activeType);
  }, [activeType]);

  const fetchCategories = async (type) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/categories?type=${type}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = formName.trim();
    if (!trimmed) return;
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/categories/${editingCategory.id}`, { name: trimmed });
        toast.success('更新成功（已同步更新所有用緊呢個類別嘅學員）');
      } else {
        await axios.post(`${API_URL}/categories`, { type: activeType, name: trimmed });
        toast.success('新增成功');
      }
      setDialogOpen(false);
      setFormName('');
      setEditingCategory(null);
      fetchCategories(activeType);
    } catch (error) {
      const msg = error.response?.data?.detail || '操作失敗';
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/categories/${deleteTarget.id}`);
      toast.success('刪除成功');
      fetchCategories(activeType);
    } catch (error) {
      toast.error('刪除失敗');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= categories.length) return;
    const reordered = [...categories];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setCategories(reordered);
    try {
      const payload = reordered.map((c, i) => ({ id: c.id, sort_order: i }));
      await axios.post(`${API_URL}/categories/reorder`, payload);
      toast.success('次序已更新');
    } catch (error) {
      toast.error('排序失敗');
      fetchCategories(activeType);
    }
  };

  const openDialog = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setFormName(cat.name);
    } else {
      setEditingCategory(null);
      setFormName('');
    }
    setDialogOpen(true);
  };

  const activeTab = TABS.find((t) => t.type === activeType);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">類別管理</h1>
          <p className="text-muted-foreground mt-1">管理學員管理中嘅各種下拉選單</p>
        </div>

        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="border-b flex overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.type}
                onClick={() => setActiveType(tab.type)}
                className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeType === tab.type
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">
                共 {categories.length} 個{activeTab?.label}
              </p>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setFormName(''); setEditingCategory(null); } }}>
                <DialogTrigger asChild>
                  <Button onClick={() => openDialog()}>
                    <Plus className="mr-2 h-4 w-4" /> 新增{activeTab?.label}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingCategory ? `編輯${activeTab?.label}` : `新增${activeTab?.label}`}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>名稱 *</Label>
                      <Input
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        placeholder={activeTab?.placeholder}
                        required
                        autoFocus
                      />
                      {editingCategory && (
                        <p className="text-xs text-muted-foreground mt-2">
                          改名會自動更新所有用緊「{editingCategory.name}」嘅學員資料。
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit">儲存</Button>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        取消
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="text-center py-12 text-muted-foreground">載入中...</div>
            ) : categories.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                未有任何{activeTab?.label}，請點擊上方按鈕新增。
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 w-28">次序</th>
                      <th className="text-left p-3">名稱</th>
                      <th className="text-right p-3 w-32">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat, index) => (
                      <tr key={cat.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMove(index, -1)}
                              disabled={index === 0}
                              title="上移"
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMove(index, 1)}
                              disabled={index === categories.length - 1}
                              title="下移"
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                        <td className="p-3 font-medium">{cat.name}</td>
                        <td className="p-3 text-right space-x-2">
                          <Button size="sm" variant="outline" onClick={() => openDialog(cat)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(cat)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除「{deleteTarget?.name}」？</AlertDialogTitle>
            <AlertDialogDescription>用緊呢個類別嘅學員會保留個值，但下次編輯時 dropdown 唔會見到。此操作不可撤銷。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminCategories;
